/**
 * Notification Inbox
 *
 * Slide-down panel anchored to the bell icon. Shows the latest lifecycle
 * notifications for the current user: cover-note decisions, policy
 * transitions, payment status changes, claim outcomes. Clicking an item
 * marks it read and navigates to actionUrl.
 */

import { Bell, Check, CheckCheck, Inbox } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib';
import { useDateFormatter } from '@/shared/hooks/use-translation';
import { useUserNotifications } from '../../hooks/use-user-notifications';
import type {
  UserNotification,
  UserNotificationSeverity,
} from '../../types/user-notifications.types';

interface NotificationInboxProps {
  isOpen: boolean;
  onClose: () => void;
}

const severityDot: Record<UserNotificationSeverity, string> = {
  INFO: 'bg-primary-500',
  SUCCESS: 'bg-success-500',
  WARNING: 'bg-warning-500',
  ERROR: 'bg-error-500',
};

export const NotificationInbox = ({ isOpen, onClose }: NotificationInboxProps) => {
  const { items, unreadCount, isLoading, error, markRead, markAllRead } = useUserNotifications();
  const { formatRelativeTime } = useDateFormatter();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleItemClick = async (n: UserNotification): Promise<void> => {
    if (!n.readAt) await markRead(n.id);
    if (n.actionUrl) {
      void navigate(n.actionUrl);
    }
    onClose();
  };

  return (
    <>
      {/* Backdrop catches outside clicks */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />

      {/* Panel — anchored top-right under the bell */}
      <div
        role="dialog"
        aria-label="Notifications"
        className={cn(
          'fixed top-16 right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)]',
          'bg-white border border-neutral-100 rounded-xl shadow-xl',
          'flex flex-col max-h-[min(560px,calc(100vh-5rem))]'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-neutral-500" />
            <h2 className="text-sm font-semibold text-neutral-900">Notifications</h2>
            {unreadCount > 0 && (
              <span className="text-xs font-medium text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => void markAllRead()}
              className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700"
              title="Mark all as read"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-neutral-400">Loading…</div>
          )}

          {error && (
            <div className="p-4 text-sm text-error-700 bg-error-50">
              Failed to load notifications: {error}
            </div>
          )}

          {!isLoading && items.length === 0 && !error && (
            <div className="p-10 text-center">
              <Inbox className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
              <p className="text-sm text-neutral-500">No notifications yet</p>
              <p className="text-xs text-neutral-400 mt-1">Lifecycle updates will appear here.</p>
            </div>
          )}

          <ul className="divide-y divide-neutral-100">
            {items.map((n) => {
              const unread = !n.readAt;
              return (
                <li key={n.id}>
                  <button
                    onClick={() => void handleItemClick(n)}
                    className={cn(
                      'w-full text-left px-4 py-3 flex gap-3 transition-colors',
                      'hover:bg-neutral-50',
                      unread && 'bg-primary-50/30'
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1.5 w-2 h-2 rounded-full flex-shrink-0',
                        unread ? severityDot[n.severity] : 'bg-neutral-200'
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm leading-snug',
                          unread ? 'font-semibold text-neutral-900' : 'text-neutral-700'
                        )}
                      >
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed line-clamp-2">
                          {n.body}
                        </p>
                      )}
                      <p className="text-[11px] text-neutral-400 mt-1">
                        {formatRelativeTime(n.createdAt)}
                      </p>
                    </div>
                    {unread && <Check className="w-4 h-4 text-neutral-300 flex-shrink-0 mt-1" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};
